import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Accordion from './accordion';
import './assets/styles/side-menu.scss';
import {
  Container,
  Content,
  ItemCount,
  ItemText,
  Language,
  MobileItem,
  Separator,
} from './assets/styles/styles';
import { exists, useWindowSize } from './utils';

import iconClose from './assets/images/icon_close.png';

const MobSideMenu = (props) => {
  const {
    setLocale,
    menuData = [],
    children = null,
    brandFilter,
    selectedBrand = [],
    businessUserRoleId,
  } = props;
  const allowLanguageChange = exists(setLocale);
  const ref = useRef();
  const size = useWindowSize();
  const [active, setActive] = useState('campaign.all');

  const closeSideMenu = () => {
    const toggleMenu = document.getElementById('mobile-menu');
    toggleMenu.style.width = '0';
  };

  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      // If user select out side it will close the window
      if (ref.current.clientWidth > 0) {
        closeSideMenu();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  });

  useEffect(() => {
    if (size.width > 768) {
      closeSideMenu();
    }
  }, [size]);

  useEffect(() => {
    closeSideMenu();
  }, [selectedBrand]);

  const getLinkProps = ({ to, disabled, state = null }) => {
    // open in new tab if it is an external link
    const disabledClass = disabled ? 'disabled-link' : '';
    let otherProps = { className: disabledClass };

    return state && Object.keys(state).length
      ? {
        ...(!to.startsWith('/') && { target: '_blank' }),
        ...otherProps,
        ...(!to.startsWith('/') && { to: { pathname: to, state } }),
        ...(to.startsWith('/') && { to: { pathname: to, state } }),
      }
      : {
        ...(!to.startsWith('/') && { target: '_blank' }),
        ...otherProps,
        ...(!to.startsWith('/') && { to: { pathname: to } }),
        ...(to.startsWith('/') && { to }),
      };
  };

  const renderMenuItems = () => {
    return menuData.map(({ sectionData }, index) => {
      return (
        <div key={index}>
          {sectionData &&
            sectionData.map((section, index) => {

              // each section of sidebar items is separated by a "separator" grey bar
              const {
                menuItem: {
                  label = '',
                  to = '/',
                  disabled = false,
                  expended,
                } = {},
                subMenuItems = [],
              } = section;
              const noChild = isEmpty(subMenuItems);
              // if there is no nested route, navigate to provided menu route
              // otherwise loop over nested routes to add their navigations

              return noChild ? (
                <Link
                  onClick={closeSideMenu}
                  key={index}
                  {...getLinkProps({ to, disabled })}
                >
                  <Accordion noChild title={label} />
                </Link>
              ) : (
                <Accordion title={label} key={index} expended={expended}>
                  {subMenuItems &&
                    subMenuItems.map((submenuItem, index) => {
                      const {
                        label = '',
                        count,
                        to = '/',
                        disabled = false,
                        name = '',
                        state = {},
                      } = submenuItem;
                      return (
                        <div key={index}>
                          {submenuItem.permissions &&
                            !submenuItem.permissions.includes(
                              businessUserRoleId
                            ) ? (
                            ''
                          ) : (
                            <Link
                              onClick={() => {
                                setActive(name);
                                closeSideMenu();
                              }}
                              key={index}
                              {...getLinkProps({
                                to,
                                disabled,
                                state,
                              })}
                            >
                              <MobileItem active={active === name}>
                                <ItemText active={active === name}>
                                  {label}
                                </ItemText>
                                {/*only render count if we are receiving the count prop*/}
                                {exists(count) ? (
                                  <ItemCount active={active === name}>
                                    {count}
                                  </ItemCount>
                                ) : null}
                              </MobileItem>
                            </Link>
                          )}
                        </div>
                      );
                    })}
                </Accordion>
              );
            })}
          <Separator />
        </div>
      );
    });
  };

  const renderLanguages = () => {
    return (
      <Fragment>
        <Language
          onClick={() => {
            setLocale('en');
            closeSideMenu();
          }}
        >
          EN
        </Language>
        <Language
          onClick={() => {
            setLocale('es');
            closeSideMenu();
          }}
        >
          ES
        </Language>
        <Separator />
      </Fragment>
    );
  };

  return (
    <Container ref={ref} id="mobile-menu">
      <Content>
        <div className="custom-cross-icon">
          <img
            onClick={closeSideMenu}
            src={iconClose}
            alt="custom-cross-icon"
          />
        </div>
        {brandFilter && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '2rem',
            }}
          >
            {brandFilter()}
          </div>
        )}
        <div>
          {menuData.length > 0 ? renderMenuItems() : null}

          {allowLanguageChange && renderLanguages()}

          {/*optionally, any remaining parent specific content goes here*/}
          {children}
        </div>
      </Content>
    </Container>
  );
};

MobSideMenu.propTypes = {
  /**
   * @param {data} contains sections of menu and sub menu items ( with label, link, count)
   * @param {children} any remaining parent specific content
   * @param {setLocale} only if language change functionality is needed
   */
  children: PropTypes.node,
  setLocale: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      sectionData: PropTypes.arrayOf(
        PropTypes.shape({
          menuItem: PropTypes.shape({
            label: PropTypes.string.isRequired,
            to: PropTypes.string,
          }),
          subMenuItems: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string.isRequired,
              count: PropTypes.number,
              to: PropTypes.string.isRequired,
            })
          ),
        })
      ),
    })
  ),
};

export { MobSideMenu };
